import { useState, useEffect, useRef } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import s from "./styles.module.scss";
import { ReactComponent as SyncIcon } from "../../assets/syncicon.svg";
import { ReactComponent as GoogleIcon } from "../../assets/blackgcalicon.svg";
import { ReactComponent as OutlookIcon } from "../../assets/blackoutlookicon.svg";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CalendarSync = ({ onSyncComplete }) => {
  const { getToken } = useAuth();
  const { user } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [providerStatus, setProviderStatus] = useState({
    google: { connected: false, syncing: false },
    outlook: { connected: false, syncing: false }
  });
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  // Fetch provider status on mount
  useEffect(() => {
    // Try to load from localStorage first for instant UI feedback
    const cachedStatus = localStorage.getItem('ocean_calendar_sync_status');
    if (cachedStatus) {
      try {
        const parsed = JSON.parse(cachedStatus);
        setProviderStatus(parsed);
      } catch (e) {
        console.error('Failed to parse cached sync status:', e);
      }
    }

    fetchProviderStatus(); // Fetch fresh data in background
  }, []);

  const fetchProviderStatus = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/calendar/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "success" && data.data?.providers) {
          const statusMap = {};
          data.data.providers.forEach(p => {
            statusMap[p.id] = { connected: p.connected, syncing: p.syncing };
          });
          setProviderStatus(statusMap);
          localStorage.setItem('ocean_calendar_sync_status', JSON.stringify(statusMap));
        }
      }
    } catch (error) {
      console.error('Error fetching provider status:', error);
    }
  };

  const showToast = (message) => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast(message);
    // Auto-dismiss after 3 seconds
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const handleProviderClick = async (providerId) => {
    const state = providerStatus[providerId];

    if (!state?.connected) {
      // Not connected - redirect to backend OAuth flow
      if (!user?.id) {
        console.error("No user ID available for OAuth");
        return;
      }

      // Redirect to backend OAuth endpoint with state (clerk user ID) and redirect URL
      // Use /sync endpoint for read-only access (less scary permissions)
      const redirectUrl = encodeURIComponent("/calendar");
      const oauthUrl = `${API_BASE}/${providerId}/login/sync?state=${user.id}&redirect=${redirectUrl}`;
      window.location.href = oauthUrl;
      return;
    }

    if (state.syncing) {
      // Currently syncing - disconnect
      await disconnectProvider(providerId);
      return;
    }

    // Connected but sync not set up - set up webhook subscription
    await setupSync(providerId);
  };

  const disconnectProvider = async (providerId) => {
    setIsSyncing(true);

    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/calendar/disconnect/${providerId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchProviderStatus();
        const providerName = providerId === 'google' ? 'Google Calendar' : 'Outlook Calendar';
        showToast(`${providerName} disconnected`);
      } else {
        const error = await res.json();
        console.error('Disconnect failed:', error.message);
      }
    } catch (error) {
      console.error('Disconnect failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const setupSync = async (providerId) => {
    setIsSyncing(true);

    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/calendar/setup-sync/${providerId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        // Refresh provider status
        await fetchProviderStatus();
        onSyncComplete?.();
      } else {
        const error = await res.json();
        console.error('Setup sync failed:', error.message);
      }
    } catch (error) {
      console.error('Setup sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getProviderState = (providerId) => {
    return providerStatus[providerId] || { connected: false, syncing: false };
  };

  return (
    <div className={s.calendarSync}>
      <button
        className={`${s.syncButton} ${isSyncing ? s.spinning : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        title="Calendar sync"
      >
        <SyncIcon />
      </button>

      {isExpanded && (
        <div className={s.providerIcons}>
          {/* Google */}
          {(() => {
            const state = getProviderState('google');
            return (
              <button
                className={`${s.providerButton} ${state.syncing ? s.connected : s.disconnected}`}
                onClick={() => handleProviderClick('google')}
                title={
                  !state.connected
                    ? "Connect Google Calendar"
                    : state.syncing
                    ? "Google Calendar syncing (click to disconnect)"
                    : "Click to enable Google Calendar sync"
                }
              >
                <GoogleIcon />
              </button>
            );
          })()}

          {/* Outlook */}
          {(() => {
            const state = getProviderState('outlook');
            return (
              <button
                className={`${s.providerButton} ${state.syncing ? s.connected : s.disconnected}`}
                onClick={() => handleProviderClick('outlook')}
                title={
                  !state.connected
                    ? "Connect Outlook Calendar"
                    : state.syncing
                    ? "Outlook Calendar syncing (click to disconnect)"
                    : "Click to enable Outlook Calendar sync"
                }
              >
                <OutlookIcon />
              </button>
            );
          })()}
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={s.toast}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default CalendarSync;
